import { useAppDispatch, useAppSelector } from 'store/hooks';
import React from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import {
    ACTION,
    FORGOT_YOUR_PASSWORD,
    FORGOT_YOUR_PASSWORD_CONFIRM,
    HEALTH,
    JOIN,
    OAUTH_PROVIDER_INSTALL,
    SIGN_IN,
    SIGN_UP,
    UNAUTHORIZED
} from 'config/routes';
import { PrivateRoute } from 'lib/PrivateRoute';
import ForgotYourPassword from 'pages/forgot-your-password';
import ForgotYourPasswordConfirm from 'pages/forgot-your-password/confirm';
import Health from 'pages/health';
import Join from 'pages/join';
import { OAuthInstallPage } from 'pages/oauth/install';
import SignIn from 'pages/sign-in';
import SignUp from 'pages/sign-up';
import Unathorized from 'pages/unauthorized';
import { Throbber } from 'ui/Throbber';
import ActionPage from './action';
import Authorized from './authorized';
import { getPublicAndPrivateSettings, resetPublicSettings } from 'store/settings/settingsSlice';
import { Userpilot } from 'userpilot';
import { auth } from 'lib/firebase/firebase';

interface RootProps {
    isFirebaseLoaded: boolean;
}

const RootPure: React.FC<RootProps> = ({ isFirebaseLoaded }) => {
    const dispatch = useAppDispatch();
    const { tenant, claims } = useAppSelector(state => state.auth);
    const { settings } = useAppSelector(state => state.settings);
    const location = useLocation();

    React.useEffect(() => {
        if (isFirebaseLoaded && import.meta.env.NODE_ENV === 'production') {
            // For userpilot to properly work we need to send the updates on the page change
            Userpilot.reload();
        }
    }, [isFirebaseLoaded, location]);

    React.useEffect(() => {
        if (isFirebaseLoaded && tenant?._id) {
            dispatch(getPublicAndPrivateSettings(claims));
        }
        return () => {
            dispatch(resetPublicSettings());
        };
    }, [claims, dispatch, isFirebaseLoaded, tenant?._id]);
    // Refresh tenant/user information
    React.useEffect(() => {
        const identifyUserpilot = async () => {
            const currentUser = auth().currentUser;
            if (currentUser) {
                const tokenResult = await currentUser.getIdTokenResult();
                const { email, name } = tokenResult.claims;
                if (import.meta.env.NODE_ENV === 'production' && isFirebaseLoaded && settings && claims) {
                    Userpilot.identify(currentUser.uid, {
                        name,
                        email,
                        rId: claims.roleId,
                        created_at: currentUser.metadata.creationTime,
                        company: {
                            id: claims.tenantId,
                            name: tenant?.title,
                            premiumLoyaltyEnabled: !!settings?.premiumLoyaltyEnabled,
                            scheduledNotificationsEnabled: !!settings?.scheduledNotificationsEnabled,
                            triggeredNotificationsEnabled: !!settings?.triggeredNotificationsEnabled
                        }
                    });
                }
            }
        };
        try {
            identifyUserpilot();
        } catch {
            // do nothing
        }
    }, [
        claims,
        claims?.roleId,
        claims?.tenantId,
        isFirebaseLoaded,
        settings,
        settings?.premiumLoyaltyEnabled,
        settings?.scheduledNotificationsEnabled,
        settings?.triggeredNotificationsEnabled,
        tenant?.title
    ]);
    if (!isFirebaseLoaded) {
        return <Throbber />;
    }
    return (
        <Routes>
            <Route path={ACTION} element={<ActionPage />} />
            <Route path={HEALTH} element={<Health />} />
            <Route path={UNAUTHORIZED} element={<Unathorized />} />
            <Route path={SIGN_IN} element={<SignIn />} />
            <Route path={SIGN_UP} element={<SignUp />} />
            <Route path={FORGOT_YOUR_PASSWORD} element={<ForgotYourPassword />} />
            <Route path={FORGOT_YOUR_PASSWORD_CONFIRM} element={<ForgotYourPasswordConfirm />} />
            <Route path={JOIN} element={<Join />} />
            <Route element={<PrivateRoute />}>
                {/* Hidden forwarder: authed, but rendered without the console chrome */}
                <Route path={OAUTH_PROVIDER_INSTALL} element={<OAuthInstallPage />} />
                <Route path="/*" element={<Authorized />} />
            </Route>
        </Routes>
    );
};

export const Root = React.memo(RootPure);
